import { useEffect, useRef } from "react";
import { refreshAccessTokenShared } from "@/data-access/core/tokenManagement";
import { decodeToken } from "@/lib/auth/tokenUtils";
import useAuth from "@/store/auth.slice";
import { handleSessionExpired } from "@/store/sessionTeardown";

const REFRESH_BUFFER_MS = 60000; // Refresh token 1 minute before expiration

export const useTokenManager = () => {
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loggedIn = useAuth((state) => state.loggedIn);

  const clearRefreshTimeout = () => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  };

  const setupRefreshTimer = () => {
    clearRefreshTimeout();

    const { accessToken } = useAuth.getState();
    if (!accessToken) return;

    const decoded = decodeToken(accessToken);
    if (!decoded) return;

    const expirationMs = decoded.exp * 1000; // Convert to milliseconds
    const timeUntilRefresh = expirationMs - Date.now() - REFRESH_BUFFER_MS;

    const runScheduledRefresh = async () => {
      const { refreshToken: rt } = useAuth.getState();
      if (!rt) {
        handleSessionExpired();
        return;
      }

      // Same refresh path + in-flight dedup as GraphQL / WebSocket (getAuthToken).
      const access = await refreshAccessTokenShared();
      if (access) {
        setupRefreshTimer();
      } else {
        handleSessionExpired();
      }
    };

    if (timeUntilRefresh <= 0) {
      // Token is already expired or will expire very soon
      void runScheduledRefresh();
    } else {
      refreshTimeoutRef.current = setTimeout(
        () => void runScheduledRefresh(),
        timeUntilRefresh,
      );
    }
  };

  useEffect(() => {
    if (!loggedIn) return;

    setupRefreshTimer();

    // Browser timers are throttled (or paused) in background tabs and during
    // device sleep. Re-evaluate the refresh schedule whenever the tab becomes
    // visible so a returning user never hits an expired token mid-session.
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setupRefreshTimer();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearRefreshTimeout();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [loggedIn]);
};
